import {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
  useRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  Heart,
  XCircle,
  AlertCircle,
  Info,
  X,
  Sparkles,
  Bell,
} from "lucide-react";

// Toast Context
const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};

// Toast Types
const toastTypes = {
  success: {
    icon: CheckCircle,
    gradient: "from-green-500 to-emerald-500",
    bgGradient: "from-green-500/15 to-emerald-500/15",
    borderColor: "border-green-500/30",
    textColor: "text-green-600 dark:text-green-400",
    iconBg: "bg-gradient-to-br from-green-500 to-emerald-500",
    ringColor: "ring-green-500/20",
  },
  error: {
    icon: XCircle,
    gradient: "from-red-500 to-rose-500",
    bgGradient: "from-red-500/15 to-rose-500/15",
    borderColor: "border-red-500/30",
    textColor: "text-red-600 dark:text-red-400",
    iconBg: "bg-gradient-to-br from-red-500 to-rose-500",
    ringColor: "ring-red-500/20",
  },
  warning: {
    icon: AlertCircle,
    gradient: "from-amber-500 to-orange-500",
    bgGradient: "from-amber-500/15 to-orange-500/15",
    borderColor: "border-amber-500/30",
    textColor: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-gradient-to-br from-amber-500 to-orange-500",
    ringColor: "ring-amber-500/20",
  },
  info: {
    icon: Info,
    gradient: "from-blue-500 to-cyan-500",
    bgGradient: "from-blue-500/15 to-cyan-500/15",
    borderColor: "border-blue-500/30",
    textColor: "text-blue-600 dark:text-blue-400",
    iconBg: "bg-gradient-to-br from-blue-500 to-cyan-500",
    ringColor: "ring-blue-500/20",
  },
  wishlist: {
    icon: Heart,
    gradient: "from-rose-500 to-pink-500",
    bgGradient: "from-rose-500/15 to-pink-500/15",
    borderColor: "border-rose-500/30",
    textColor: "text-rose-600 dark:text-rose-400",
    iconBg: "bg-gradient-to-br from-rose-500 to-pink-500",
    ringColor: "ring-rose-500/20",
  },
};

// Individual Toast Component
const ToastItem = ({ id, type, message, onClose, onHeightChange }) => {
  const ToastIcon = toastTypes[type]?.icon || Info;
  const styles = toastTypes[type] || toastTypes.info;
  const toastRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, 4500);
    return () => clearTimeout(timer);
  }, [id, onClose]);

  // Report height to parent for better positioning
  useEffect(() => {
    if (toastRef.current && onHeightChange) {
      onHeightChange(id, toastRef.current.offsetHeight);
    }
  }, [id, onHeightChange]);

  return (
    <motion.div
      ref={toastRef}
      layout
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{
        duration: 0.4,
        type: "spring",
        stiffness: 400,
        damping: 30,
        mass: 0.8,
      }}
      className="group relative overflow-hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-800/30 shadow-2xl shadow-black/10 w-[380px] max-w-[calc(100vw-2rem)]"
    >
      {/* Animated Gradient Border */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Gradient Background */}
      <div
        className={`absolute inset-0 bg-gradient-to-r ${styles.bgGradient} opacity-60`}
      />

      {/* Content */}
      <div className="relative flex items-start gap-3 p-4 pr-12">
        {/* Animated Icon Container */}
        <motion.div
          initial={{ scale: 0.8, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: "spring",
            stiffness: 500,
            damping: 20,
            delay: 0.1,
          }}
          className={`relative flex-shrink-0 p-2 rounded-xl ${styles.iconBg} shadow-lg ring-2 ${styles.ringColor}`}
        >
          <ToastIcon size={18} className="text-white drop-shadow-sm" />
        </motion.div>

        {/* Message Content */}
        <div className="flex-1 min-w-0">
          <p
            className={`text-sm font-semibold ${styles.textColor} mb-0.5 capitalize`}
          >
            {type === "wishlist" ? "Wishlist" : type}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed break-words">
            {message}
          </p>
        </div>

        {/* Close Button with Hover Effect */}
        <button
          onClick={() => onClose(id)}
          className="absolute right-3 top-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-110"
        >
          <X size={14} className="transition-transform" />
        </button>
      </div>

      {/* Progress Bar with Gradient */}
      <motion.div
        initial={{ width: "100%" }}
        animate={{ width: "0%" }}
        transition={{ duration: 4.5, ease: "linear" }}
        className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${styles.gradient}`}
      />

      {/* Shine Effect on Hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
      </div>
    </motion.div>
  );
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastCountRef = useRef(0);
  const MAX_TOASTS = 3; // Maximum

  // Get a unique key
  const getToastKey = (message, type) =>
    `${type}:${message.toLowerCase().trim()}`;

  const addToast = useCallback(
    (message, type = "info") => {
      const toastKey = getToastKey(message, type);

      // Check if a toast with the same message and type already exists
      const existingToast = toasts.find(
        (toast) => getToastKey(toast.message, toast.type) === toastKey,
      );

      // If duplicate found
      if (existingToast) {
        return existingToast.id;
      }

      const now = Date.now();

      // If reached the maximum number of toasts
      if (toasts.length >= MAX_TOASTS) {
        setToasts((prev) => {
          const newToasts = [...prev];
          newToasts.pop();
          return [{ id: now, message, type }, ...newToasts];
        });
        return now;
      }

      // Add new toast to the beginning
      setToasts((prev) => [{ id: now, message, type }, ...prev]);

      return now;
    },
    [toasts],
  );

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback(
    (message) => addToast(message, "success"),
    [addToast],
  );
  const error = useCallback(
    (message) => addToast(message, "error"),
    [addToast],
  );
  const warning = useCallback(
    (message) => addToast(message, "warning"),
    [addToast],
  );
  const info = useCallback((message) => addToast(message, "info"), [addToast]);
  const wishlist = useCallback(
    (message) => addToast(message, "wishlist"),
    [addToast],
  );

  // Helper to clear all toasts
  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider
      value={{
        addToast,
        removeToast,
        success,
        error,
        warning,
        info,
        wishlist,
        clearAll,
        activeCount: toasts.length,
        maxToasts: MAX_TOASTS,
      }}
    >
      {children}

      {/* Toast Container */}
      <div
        className="fixed z-[100] flex flex-col-reverse gap-3 
        bottom-4 left-1/2 -translate-x-1/2 
        sm:bottom-auto sm:top-24 sm:right-6 sm:left-auto sm:translate-x-0 sm:flex-col"
      >
        <AnimatePresence mode="popLayout">
          {toasts.map((toast) => (
            <ToastItem
              key={toast.id}
              id={toast.id}
              type={toast.type}
              message={toast.message}
              onClose={removeToast}
            />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
